/**
 * Store Zustand pour les rôles
 * Cache local + état UI pour les pages de gestion des rôles
 */

import { create } from 'zustand';
import type { RoleDTO, PermissionDTO } from '../../application/dto/dtos';

export interface RolesState {
  // Sélection courante (pour les pages Create/Edit)
  selectedRole: RoleDTO | null;

  // État UI
  isFormOpen: boolean;
  isDeleteDialogOpen: boolean;
  roleASupprimer: RoleDTO | null;

  // Filtres locaux
  filtreNiveau: number | null;
  filtreActif: boolean | null;
  searchTerm: string;

  // Actions
  selectRole: (role: RoleDTO | null) => void;
  ouvrirFormulaire: (role?: RoleDTO) => void;
  fermerFormulaire: () => void;
  ouvrirConfirmationSuppression: (role: RoleDTO) => void;
  fermerConfirmationSuppression: () => void;
  setFiltreNiveau: (niveau: number | null) => void;
  setFiltreActif: (actif: boolean | null) => void;
  setSearchTerm: (term: string) => void;
  resetFiltres: () => void;
}

export const useRolesStore = create<RolesState>()((set) => ({
  // État initial
  selectedRole: null,
  isFormOpen: false,
  isDeleteDialogOpen: false,
  roleASupprimer: null,
  filtreNiveau: null,
  filtreActif: null,
  searchTerm: '',

  // Actions
  selectRole: (role) => set({ selectedRole: role }),

  ouvrirFormulaire: (role) => set({
    selectedRole: role ?? null,
    isFormOpen: true,
  }),

  fermerFormulaire: () => set({
    isFormOpen: false,
    selectedRole: null,
  }),

  ouvrirConfirmationSuppression: (role) => set({
    roleASupprimer: role,
    isDeleteDialogOpen: true,
  }),

  fermerConfirmationSuppression: () => set({
    roleASupprimer: null,
    isDeleteDialogOpen: false,
  }),

  setFiltreNiveau: (niveau) => set({ filtreNiveau: niveau }),
  setFiltreActif: (actif) => set({ filtreActif: actif }),
  setSearchTerm: (term) => set({ searchTerm: term }),

  resetFiltres: () => set({
    filtreNiveau: null,
    filtreActif: null,
    searchTerm: '',
  }),
}));

// ============================================================
// Utilitaires pour grouper les permissions par ressource
// ============================================================

export interface PermissionsParRessource {
  ressource: string;
  permissions: PermissionDTO[];
}

export function grouperPermissionsParRessource(
  permissions: PermissionDTO[],
): PermissionsParRessource[] {
  const map = new Map<string, PermissionDTO[]>();

  for (const perm of permissions) {
    const existing = map.get(perm.ressource) ?? [];
    map.set(perm.ressource, [...existing, perm]);
  }

  return Array.from(map.entries()).map(([ressource, perms]) => ({
    ressource,
    permissions: perms.sort((a, b) => a.action.localeCompare(b.action)),
  }));
}
